/* eslint-disable consistent-return */
import { getApiPath, apiPaths, httpStatus } from '../../constants'
import { Api } from '../../middlewares'
import { Logger, LoggerTypes } from '../../utils'

// -- CREATE
export const CREATE_PROJECT_SUCCESS = 'CREATE_PROJECT_SUCCESS'
export const CREATE_PROJECT_FAILURE = 'CREATE_PROJECT_FAILURE'

// -- UPDATE
export const UPDATE_PROJECT_SUCCESS = 'UPDATE_PROJECT_SUCCESS'
export const UPDATE_PROJECT_FAILURE = 'UPDATE_PROJECT_FAILURE'

// -- GET (array of objects)
export const LIST_PROJECT_SUCCESS = 'LIST_PROJECT_SUCCESS'
export const LIST_PROJECT_FAILURE = 'LIST_PROJECT_FAILURE'

// -- GET (one object)
export const GET_PROJECT_SUCCESS = 'GET_PROJECT_SUCCESS'
export const GET_PROJECT_FAILURE = 'GET_PROJECT_FAILURE'

// -- DELETE
export const DELETE_PROJECT_SUCCESS = 'DELETE_PROJECT_SUCCESS'
export const DELETE_PROJECT_FAILURE = 'DELETE_PROJECT_FAILURE'

// -- SEARCH
export const SEARCH_PROJECTS = 'SEARCH_PROJECTS'

// -- IMPORT GLOBAL ATTRIBUTES
export const IMPORT_PROJECT_GLOBALS_SUCCESS = 'IMPORT_PROJECT_GLOBALS_SUCCESS'
export const IMPORT_PROJECT_GLOBALS_FAILURE = 'IMPORT_PROJECT_GLOBALS_FAILURE'

// -- IMPORT GLOBAL WEATHER
// export const IMPORT_PROJECT_WEATHER_SUCCESS = 'IMPORT_PROJECT_WEATHER_SUCCESS'
// export const IMPORT_PROJECT_WEATHER_FAILURE = 'IMPORT_PROJECT_WEATHER_FAILURE'

// ____________________________________________________________

/**
 * Searching a project.
 *
 * @param {object} terms to be searched.
 * @returns {object}
 */
export const searchProjects = (terms) => ({
  type: SEARCH_PROJECTS,
  data: terms
})

/**
 * Create a new project broadcast two types of events:
 * - CREATE_PROJECT_SUCCESS: in case of success.
 * - CREATE_PROJECT_FAILURE: in case of failure.
 *
 * @param {object} project Project to create.
 * @returns {Promise<object>}
 */
export const createProject = (project) => async (dispatch) => {
  const api = new Api(apiPaths.PROJECT_CREATE)
  const result = await api.create(project)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] projectsActions.createProject()', { result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CREATE_PROJECT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: CREATE_PROJECT_FAILURE,
    data: result.data
  })
}

/**
 * Update the values of a project broadcast two types of events:
 * - UPDATE_PROJECT_SUCCESS: in case of success.
 * - UPDATE_PROJECT_FAILURE: in case of failure.
 *
 * @param {object} project Project to update
 * @returns {Promise<object>}
 */
export const updateProject = (project) => async (dispatch) => {
  if (!project) {
    return
  }

  const path = getApiPath(apiPaths.PROJECT_UPDATE, { pid: project.id })
  const api = new Api(path)

  let newProject = Object.assign({}, project)
  newProject.update_user = 'test' // FIX: a bug that prevent updating a project without the attribute `update_user` set

  const result = await api.update(newProject)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] projectsActions.updateProject()', { path, result, newProject })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: UPDATE_PROJECT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: UPDATE_PROJECT_FAILURE,
    data: result.data
  })
}

/**
 * Get the project lists form the API and broadcast two types of events:
 * - LIST_PROJECT_SUCCESS: in case of success.
 * - LIST_PROJECT_FAILURE: in case of failure.
 *
 * @returns {Promise<object>}
 */
export const getProjects = () => async (dispatch) => {
  const path = apiPaths.PROJECT_LIST
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] projectsActions.getProjects()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: LIST_PROJECT_SUCCESS,
      data: result.data.items || []
    })
  }
  return dispatch({
    type: LIST_PROJECT_FAILURE,
    data: result.data
  })
}

/**
 * Get a specific project object form an pid, from the API and broadcast two types of events:
 * - GET_PROJECT_SUCCESS: in case of success.
 * - GET_PROJECT_FAILURE: in case of failure.
 *
 * @param {number} pid ProjectId to get
 * @returns {Promise<object>}
 */
export const getProject = (pid) => async (dispatch) => {
  const path = getApiPath(apiPaths.PROJECT_GET, { pid })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] projectsActions.getProject()', { path, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: GET_PROJECT_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: GET_PROJECT_FAILURE,
    data: result.data
  })
}

/**
 * Delete a project and broadcast two types of events:
 * - DELETE_PROJECT_SUCCESS: in case of success.
 * - DELETE_PROJECT_FAILURE: in case of failure.
 *
 * @param {Object} project to delete
 * @returns {Promise<object>}
 */
export const deleteProject = (project) => async (dispatch) => {
  const path = getApiPath(apiPaths.PROJECT_DELETE, { pid: project.id })
  const api = new Api(path)
  const result = await api.delete()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] projectsActions.deleteProject()', { path, project, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: DELETE_PROJECT_SUCCESS,
      data: project
    })
  }
  return dispatch({
    type: DELETE_PROJECT_FAILURE,
    data: result.data
  })
}

/**
 * Import a list of global attribute Ids within a project and broadcast two type of events:
 * - IMPORT_PROJECT_GLOBALS_SUCCESS: in case of success.
 * - IMPORT_PROJECT_GLOBALS_FAILURE: in case of failure.
 *
 * @param {Object} listOfIds List of global attributes UUID to import.
 * @param {number} pid ProjectId UUID.
 * @returns {Promise<object>}
 */
export const importProjectGlobals = (listOfIds, pid) => async (dispatch) => {
  const path = getApiPath(apiPaths.PROJECTS_IMPORT_GLOBAL, { pid })
  const api = new Api(path)
  const result = await api.update(listOfIds)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] projectsActions.importProjectGlobals()', { path, listOfIds })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: IMPORT_PROJECT_GLOBALS_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: IMPORT_PROJECT_GLOBALS_FAILURE,
    data: result.data
  })
}
